import { queryClient, queryClientRehydrated } from '@fhs/client';
import { nativeHomepageHeroQuery } from '@fhs/cms-groq-queries';
import { performSanityQuery } from '@fhs/cms-groq-queries/src/client';

import {
  pruneStaleHeroCache,
  warmHeroVideoCache,
} from './hero-video-cache';
import {
  EVERGREEN_VIDEO_KEY,
  isHeroVideoLive,
  preloadHeroVideo,
  resolveHeroVideoSource,
} from './hero-video-preload';
import {
  EVERGREEN_SAFETY_CAP_MS,
  isHeroFramePainted,
  isHeroVideoErrored,
  reportHeroVideoFellBack,
  waitForHeroSignal,
} from './hero-video-ready-signal';

export type NativeHomepageHero = {
  isLtoActive?: boolean | null;
  ltoName?: string | null;
  videoUrl?: string | null;
} | null;

// How soon a CMS edit (LTO toggle / swapped video) is picked up.
const HERO_STALE_TIME_MS = __DEV__ ? 0 : 1000 * 60 * 5;

// Bounded window `waitForHeroSplashReady` waits for the in-flight CMS query to
// resolve before picking a branch. Matches the LTO paint cap so the splash
// budgets "decide which video" and "paint that video" symmetrically. Tuned
// after real-device testing showed a ~5% wifi tail where the CDN-backed Sanity
// query landed just past the previous 500ms cap and the splash mistakenly
// committed to evergreen even though LTO was seconds away.
const CMS_RESOLVE_CAP_MS = 1000;

// Bounded window we wait for the react-query persister to finish rehydrating
// last session's cache off disk. If the persister lands within this window we
// see LTO in the cache and pick it deterministically; if not, we fall through
// to the CMS wait. Kept short — AsyncStorage reads are typically well under
// 100ms, and this is off the critical path for first-ever installs.
const PERSISTER_REHYDRATION_CAP_MS = 250;

// Kicks off warming a hero video's byte fetch as early as possible. Safe to
// call any time — the preload layer dedupes by key. Extracted so both the
// queryFn (CMS path) and `prefetchNativeHomepageHero` (persister path) use the
// same warming logic, and so preload can happen *synchronously* the moment
// we know the URL, before subscribers can re-render and mount the layer cold.
function warmHeroVideoIfActive(hero: NativeHomepageHero | undefined): void {
  const key = selectHeroVideoKey(hero);
  // Only prune the on-disk cache while no player has taken over yet — deleting
  // a cached file that an active AVPlayer is reading from can stall playback.
  // If we skip pruning here (mid-session campaign swap), the next launch's
  // bootstrap prune (also gated by this flag) will clean up the stale file.
  const canPrune = !isHeroVideoLive();
  if (key === EVERGREEN_VIDEO_KEY) {
    if (canPrune) pruneStaleHeroCache(null);
    return;
  }
  preloadHeroVideo(key, resolveHeroVideoSource(key));
  if (canPrune) pruneStaleHeroCache(key);
  // Fire-and-forget download to seed the on-disk cache for the *next* launch.
  // No-op if the file is already cached — in which case `resolveHeroVideoSource`
  // above already returned the local URI and this launch paints from disk.
  void warmHeroVideoCache(key);
}

// Race a promise against a timeout; resolves as soon as either wins.
function withTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>(resolve => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    promise.then(done, done);
    setTimeout(done, ms);
  });
}

export const nativeHomepageHeroQueryOptions = {
  queryKey: ['native-homepage-hero-v0'] as const,
  queryFn: async () => {
    // Route via the CDN-backed client: ~seconds of edge staleness is a fine
    // trade for a much faster response, and this query is on the splash's
    // critical path where every millisecond widens or narrows the race window.
    const result = await performSanityQuery<NativeHomepageHero>({
      groqQueryString: nativeHomepageHeroQuery,
      useCdn: true,
    });
    // Kick off the LTO video preload synchronously here — before any subscriber
    // re-renders and mounts the layer cold. This is the earliest possible
    // moment we know the URL, so we shave off every millisecond of network+decode
    // that would otherwise blow the splash's LTO paint budget.
    warmHeroVideoIfActive(result);
    return result;
  },
  // Serve the persisted cache instantly (paints on warm/cold starts), then
  // revalidate in the background; new data crossfades in via the widget.
  staleTime: HERO_STALE_TIME_MS,
  refetchOnMount: true,
  refetchOnReconnect: true,
  meta: { persist: true },
};

// Returns the string key used by the video stack/cache.
export function selectHeroVideoKey(hero: NativeHomepageHero | undefined): string {
  if (hero?.isLtoActive && hero.videoUrl) {
    return hero.videoUrl;
  }
  return EVERGREEN_VIDEO_KEY;
}

// The key the home widget will try to show first, read from the same cache the
// widget reads. Lets the splash and the widget agree on the selected source.
export function selectCurrentHeroVideoKey(): string {
  const hero = queryClient.getQueryData<NativeHomepageHero>(
    nativeHomepageHeroQueryOptions.queryKey
  );
  return selectHeroVideoKey(hero);
}

// Holds the splash until the first hero video is definitively painting, so it
// never reveals a black frame:
//   - No LTO selected → wait (safety-capped) for evergreen to paint.
//   - LTO selected → give it `ltoCapMs` to paint or error; if it wins, reveal on
// Else the widget falls back to evergreen, so wait for that.
export async function waitForHeroSplashReady(ltoCapMs: number): Promise<void> {
  const evergreenReady = () =>
    isHeroFramePainted(EVERGREEN_VIDEO_KEY) || isHeroVideoErrored(EVERGREEN_VIDEO_KEY);

  // Give the persister a bounded chance to finish rehydrating so a returning
  // user's LTO data is in the cache before we decide the branch. Without this,
  // `getQueryData` can read a still-empty cache on cold-start even when disk
  // has last session's LTO ready to serve.
  await withTimeout(queryClientRehydrated, PERSISTER_REHYDRATION_CAP_MS);

  // Give the in-flight CMS query a bounded chance to land so the branch decision
  // below reflects real state, not a still-empty cold cache. Racing the promise
  // against a timeout means we never hang the splash on a slow Sanity fetch.
  const cmsPromise = queryClient
    .ensureQueryData(nativeHomepageHeroQueryOptions)
    .catch(() => undefined as NativeHomepageHero | undefined);
  await withTimeout(cmsPromise, CMS_RESOLVE_CAP_MS);

  const selected = selectCurrentHeroVideoKey();

  if (selected === EVERGREEN_VIDEO_KEY) {
    // Commit: if the CMS eventually resolves with LTO after we've decided
    // evergreen, keep this session on evergreen rather than hot-swapping. That
    // mid-session swap is what surfaces as the evergreen→LTO flash. The next
    // launch's persister rehydration will reveal on LTO directly, no flash.
    void cmsPromise.then(hero => {
      const lateKey = selectHeroVideoKey(hero);
      if (lateKey !== EVERGREEN_VIDEO_KEY) {
        reportHeroVideoFellBack(lateKey);
      }
    });

    await waitForHeroSignal(evergreenReady, EVERGREEN_SAFETY_CAP_MS);
    return;
  }

  await waitForHeroSignal(
    () => isHeroFramePainted(selected) || isHeroVideoErrored(selected),
    ltoCapMs
  );
  if (isHeroFramePainted(selected)) {
    return; // LTO won the race — reveal on it
  }

  // LTO slow or errored — the widget is falling back to evergreen.
  await waitForHeroSignal(evergreenReady, EVERGREEN_SAFETY_CAP_MS);
}

// Fire the Sanity request at app startup so it's in flight before the home screen
// mounts. On a warm start the persisted data is still fresh, so this is a no-op.
export async function prefetchNativeHomepageHero(): Promise<void> {
  // Warm the evergreen video first, without awaiting anything.
  preloadHeroVideo(EVERGREEN_VIDEO_KEY, resolveHeroVideoSource(EVERGREEN_VIDEO_KEY));

  // Kick off the CMS fetch immediately, in parallel with waiting for the
  // persister. This runs at module load — before React mounts, so the persister
  // isn't even rehydrating yet — so awaiting persister first would just idle
  // the network for up to PERSISTER_REHYDRATION_CAP_MS. React-query dedupes:
  // when the persister later rehydrates fresh data, the in-flight fetch either
  // becomes a background revalidation or resolves as the source of truth for a
  // fresh install. The `queryFn` calls `warmHeroVideoIfActive` on its result,
  // so LTO byte-fetch begins the instant the URL is known.
  const cmsPromise = queryClient.prefetchQuery(nativeHomepageHeroQueryOptions);

  // Wait (bounded) for the persister to rehydrate so we can warm a returning
  // user's LTO video from disk in parallel with the CMS fetch above. A slow
  // AsyncStorage read never hangs startup — the timeout falls through.
  await withTimeout(queryClientRehydrated, PERSISTER_REHYDRATION_CAP_MS);
  const persistedHero = queryClient.getQueryData<NativeHomepageHero>(
    nativeHomepageHeroQueryOptions.queryKey
  );
  warmHeroVideoIfActive(persistedHero);

  await cmsPromise;
}
